import { AppError, type EventStreamPort, type EventStreamRequest } from '@sunucupark/client-core';
import { EVENT_STREAM_CONTENT_TYPE } from '@sunucupark/shared/lite';

/**
 * Reads the gateway's event stream with `fetch`: `EventSource` cannot carry an
 * `Authorization` header, and the stream is authenticated like every other
 * route (ADR 0008). The chunks are decoded and handed to the core's parser.
 */
export class FetchEventStream implements EventStreamPort {
  constructor(private readonly fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis)) {}

  async *open(request: EventStreamRequest): AsyncIterable<string> {
    const response = await this.fetchImpl(request.url, {
      headers: { ...request.headers, Accept: EVENT_STREAM_CONTENT_TYPE },
      signal: request.signal,
      credentials: 'omit',
      cache: 'no-store',
    });
    if (!response.ok || !response.body) {
      throw AppError.fromBody(
        {
          error: {
            code: response.status === 401 ? 'UNAUTHORIZED' : 'SERVICE_UNAVAILABLE',
            message: `Event stream refused (${response.status})`,
          },
        },
        response.status,
      );
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        yield decoder.decode(value, { stream: true });
      }
    } finally {
      reader.cancel().catch(() => undefined);
    }
  }
}
